import React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

interface DateFieldProps {
 label?: string;
 value: string; // ISO date string (yyyy-mm-dd) or empty string
 onChange: (value: string) => void; // Receives ISO date string or empty string
 placeholder?: string;
 error?: string;
 disabled?: boolean;
 required?: boolean;
}

export const DateField: React.FC<DateFieldProps> = ({
 label,
 value,
 onChange,
 placeholder = "Selecione uma data",
 error,
 disabled = false,
 required = false,
}) => {
 // Convert ISO string to Date object, or null if empty
 // Parse the date string manually to avoid timezone issues
 const validDate = React.useMemo(() => {
 if (!value) return null;
 try {
 // Parse yyyy-mm-dd format directly to avoid timezone conversion
 const [year, month, day] = value.split('-').map(Number);
 if (year && month && day) {
 const date = new Date(year, month - 1, day);
 // Verify the date is valid
 if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
 return date;
 }
 }
 } catch (e) {
 console.error('Error parsing date:', e);
 }
 return null;
 }, [value]);

 // Get today's date at midnight in local timezone
 const today = React.useMemo(() => {
 const now = new Date();
 return new Date(now.getFullYear(), now.getMonth(), now.getDate());
 }, []);

 // Function to disable dates before today
 const isDateDisabled = (date: Date) => {
 // Set time to midnight for comparison
 const dateAtMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
 return dateAtMidnight < today;
 };

 const handleDateSelect = (date: Date | undefined) => {
 if (date) {
 // Convert to ISO string (yyyy-mm-dd) using local date components
 // This avoids timezone conversion issues
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, '0');
 const day = String(date.getDate()).padStart(2, '0');
 const isoString = `${year}-${month}-${day}`;
 onChange(isoString);
 } else {
 onChange("");
 }
 };

 return (
 <div className="space-y-2">
 {label && (
 <Label htmlFor={label} className={required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
 {label}
 </Label>
 )}
 <Popover>
 <PopoverTrigger asChild>
 <Button
 variant="outline"
 className={cn(
 "w-full justify-start text-left font-normal",
 !validDate && "text-muted-foreground",
 error && "border-red-500 focus-visible:ring-red-500"
 )}
 disabled={disabled}
 type="button"
 >
 <CalendarIcon className="mr-2 h-4 w-4" />
 {validDate ? (
 format(validDate, "dd/MM/yyyy", { locale: ptBR })
 ) : (
 <span>{placeholder}</span>
 )}
 </Button>
 </PopoverTrigger>
 <PopoverContent className="w-auto p-0" align="start">
 <Calendar
 mode="single"
 selected={validDate || undefined}
 onSelect={handleDateSelect}
 initialFocus
 locale={ptBR}
 disabled={isDateDisabled}
 fromDate={today}
 />
 </PopoverContent>
 </Popover>
 {error && (
 <p className="text-sm text-red-500">{error}</p>
 )}
 </div>
 );
};

